
/**
 * Resolves the final table grouping configuration based on:
 * - selected geo grouping
 * - selected devices
 * - device aggregation setting
 * - selected payment types
 *
 * Logic:
 * - If no devices are selected:
 *   - when `shouldSum` is enabled, only geo grouping is applied
 *   - otherwise, geo + device type grouping is applied
 *
 * - If devices are selected:
 *   - when `shouldSum` is enabled, geo + device type grouping is applied
 *   - otherwise, geo + device + device type grouping is applied
 *
 * Additionally determines whether device-related
 * and payment type columns should be visible in the table.
 *
 * @param params - Configuration used to resolve grouping
 * @param params.geoGroup - Selected geo grouping
 * @param params.selectedDevices - List of selected devices
 * @param params.selectedPaymentTypes - List of selected payment types
 * @param params.shouldSum - Indicates whether data should be summed by device
 *
 * @returns Resolved grouping configuration
 * - `groupBy` - Final group-by hierarchy
 * - `showDevice` - Whether the device column should be displayed
 * - `showDeviceType` - Whether the device type column should be displayed
 * - `showPaymentType` - Whether the payment type column should be displayed
 */

import { StatsGroupEnum } from "../enum/StatsGroupEnum";
import { GeoClass } from "../interface/response/GeoFiltersResponse";

interface ResolveGroupByParams {
  geoGroup: StatsGroupEnum;
  selectedDevices: GeoClass[];
  selectedPaymentTypes: GeoClass[];
  shouldSum: boolean | null;
}

interface ResolveGroupByResult {
  groupBy: StatsGroupEnum[];
  showDevice: boolean;
  showDeviceType: boolean;
  showPaymentType: boolean;
}

export const resolveGroupBy = ({
geoGroup,
selectedDevices,
selectedPaymentTypes,
shouldSum,
}: ResolveGroupByParams): ResolveGroupByResult => {
const hasSelectedDevices = selectedDevices.length > 0;
const hasSelectedPaymentTypes = selectedPaymentTypes.length > 0;
 
const paymentTypeGroup = hasSelectedPaymentTypes
  ? [StatsGroupEnum.PaymentType]
  : [];
 
if (!hasSelectedDevices) {
  return shouldSum
   ? {
      groupBy: [geoGroup, ...paymentTypeGroup],
      showDevice: false,
      showDeviceType: false,
      showPaymentType: hasSelectedPaymentTypes,
     }
   : {
      groupBy: [
       geoGroup,
       StatsGroupEnum.DeviceTypeEnum,
       ...paymentTypeGroup,
      ],
      showDevice: false,
      showDeviceType: true,
      showPaymentType: hasSelectedPaymentTypes,
     };
}
 
return shouldSum
  ? {
     groupBy: [
      geoGroup,
      StatsGroupEnum.DeviceTypeEnum,
      ...paymentTypeGroup,
     ],
     showDevice: false,
     showDeviceType: true,
     showPaymentType: hasSelectedPaymentTypes,
    }
  : {
     groupBy: [
      geoGroup,
      StatsGroupEnum.Device,
      StatsGroupEnum.DeviceTypeEnum,
      ...paymentTypeGroup,
     ],
     showDevice: true,
     showDeviceType: true,
     showPaymentType: hasSelectedPaymentTypes,
    };
};